<script>
	import { onMount } from "svelte";
	import { fly } from "svelte/transition";
	import centerOfMass from "@turf/center-of-mass";

	import Filter from "./components/Filter.svelte";
	import Search from "./components/Search/Search.svelte";
	import Tooltip from "./components/Tooltip.svelte";
	import Legend from "./components/Legend.svelte";
	import MediaQuery, { breakpoint, platform, orientation } from "./components/MediaQuerySsr.svelte";
	import Map from "./map/map";

	import { loadJSON, loadMapbox, loadTopojson, loadCSV } from "./utils";
	import {
		categories,
		categoriesById,
		bins,
		binsById,
		initialBounds,
		initialBoundsTablet,
		maxBounds,
		bezirke,
		markerData,
	} from "./constants";

	// ///////////////////////////////////////////////////////////
	// props
	// ///////////////////////////////////////////////////////////
	export let name;
	export let paywall = false;

	// ///////////////////////////////////////////////////////////
	// screenshot mode
	// ///////////////////////////////////////////////////////////
	const SCREENSHOT_MODE = false;
	const PWIMAGE_MODE = false;
	// PW IMAGES WIDTHS: L --> desktop, m --> around 800px, s --> around 440px

	$: if (mapLoaded) {
		if (SCREENSHOT_MODE) {
			// map.hideLayer("labels-lors");
			// map.hideLayer("labels-bezirke");
			// hideRing();
		}
	}

	// ///////////////////////////////////////////////////////////
	// constants and definitions
	// ///////////////////////////////////////////////////////////

	let map;
	let tooltip;

	let mapZoom;

	let mapActivated = false;

	let mapLoaded = paywall;

	let labelsOrtsteile = [];
	let labelsBezirke = [];

	let highlightingColor = "#000";

	const luftbilderId = "luftbilder";
	let showLuftbilder = false;

	let dataSchools;
	let geoBezirke;
	let geoOrtsteile;
	let geoRing;

	// /////////////////////////////////////////////////
	// define categories and other data related variables
	// /////////////////////////////////////////////////
	const pointsId = "schools";
	const pointsBaseOpacity = 1;

	// /////////////////////////////////////////////////
	// color scales
	// /////////////////////////////////////////////////
	const generateMapboxExpression = (colors, domain) => {
		let expression = ["case"];

		// Check for null or undefined value
		expression.push(["==", ["get", "hours_possible_decimal"], null], binsById["no_data"].color);

		// Add conditions for the domain and corresponding colors
		domain.forEach((value, index) => {
			expression.push(["<=", ["get", "hours_possible_decimal"], value], colors[index]);
		});

		// Add default color for values above the highest domain value
		expression.push(colors[colors.length - 1]);

		return expression;
	};

	const colors = bins.filter((b) => b.id != "no_data").map((b) => b.color);
	const domain = bins.filter((b) => b.id != "no_data").map((b) => b.upperLimit);
	const mapboxColorExpression = generateMapboxExpression(colors, domain);

	// ///////////////////////////////////////////////////////////
	// ui bound parameters
	// ///////////////////////////////////////////////////////////
	export let elemMap;

	let selectedPoint;
	let hoveredPoint;

	let selectedCategoriesIds = categories.map((c) => c.id);

	// ///////////////////////////////////////////////////////////
	// show/hide luftbilder
	// ///////////////////////////////////////////////////////////
	$: highlightingColor = showLuftbilder ? "#fff" : "#000";

	$: if (mapActivated) {
		if (showLuftbilder) {
			map.showLayer(luftbilderId);
		} else {
			map.hideLayer(luftbilderId);
		}
	}

	// ///////////////////////////////////////////////////////////
	// data processing
	// ///////////////////////////////////////////////////////////

	const processData = () => {
		dataSchools.forEach((d) => {
			d.lat = +d.lat;
			d.lon = +d.lon;

			const numeric = [
				"students_total",
				"hours_needed",
				"hours_possible",
				"hours_missing",
				"hours_possible_decimal",
				"hours_possible_rank",
				"hours_possible_rank_within_type",
			];
			numeric.forEach((n) => {
				d[n] = d[n] ? +d[n] : null;
			});

			d.id = d.bsn;
		});
		console.log("DATA", dataSchools);

		categories.forEach((cat) => {
			cat.count = dataSchools.filter((d) => d.type === cat.id).length;
			cat.maxRank = dataSchools
				.filter((d) => d.type === cat.id)
				.reduce((acc, cur) => Math.max(acc, cur.hours_possible_rank_within_type), 0);
		});

		console.log("CATEGORIES", categories);

		geoBezirke.features.forEach((f) => {
			const center = centerOfMass(f).geometry.coordinates;
			f.properties.centerLng = center[0];
			f.properties.centerLat = center[1];

			f.properties.name = bezirke[f.properties["BEZ"]];

			labelsBezirke.push({
				name: `${f.properties.name}`,
				lnglat: [f.properties.centerLng, f.properties.centerLat],
			});
		});

		geoOrtsteile.features.forEach((f) => {
			labelsOrtsteile.push({
				name: `${f.properties.name}`,
				lnglat: f.geometry.coordinates,
			});
		});
	};

	// ///////////////////////////////////////////////////////////
	// initialize map
	// ///////////////////////////////////////////////////////////

	let tooltipHovered = false;
	let isTouch = false;

	const selectPoint = (feature) => {
		// check if feature is a geojson feature or a point
		const p = feature.geometry ? feature.properties : feature;

		selectedPoint = p;
		stopSearch(selectedPoint.name);
		addPopup(selectedPoint);
	};

	const clearPoint = () => {
		selectedPoint = undefined;
		removePopup();
		stopSearch();
	};

	onMount(async () => {
		if (paywall) return;

		const promises = [
			loadJSON(`${PATH}assets/data/berlin_bezirke.geo.json`),
			loadJSON(`${PATH}assets/data/ring.geo.json`),
			loadJSON(`${PATH}assets/data/ortsteile_labels.geo.json`),
			loadCSV(`${PATH}assets/data/schools.csv`),
			loadTopojson(),
			loadMapbox(),
		];

		function checkTouch() {
			isTouch = true;
			document.querySelector("body").removeEventListener("touchstart", checkTouch);
		}
		document.querySelector("body").addEventListener("touchstart", checkTouch);

		[geoBezirke, geoRing, geoOrtsteile, dataSchools] = await Promise.all(promises);

		processData();

		map = new Map(elemMap, {
			useFranklin: true,
			initialBounds: platform === "tablet" ? initialBoundsTablet : initialBounds,
			maxBounds,
			maxZoom: 19,
			controls: !SCREENSHOT_MODE && !PWIMAGE_MODE,
			controlsLeft: true,
			scale: true,
			popup: false,
			popupShift: false,
			labels: !SCREENSHOT_MODE,
			styleJson: `${PATH}assets/style.json`,
			hideBackgroundMap: SCREENSHOT_MODE,
		});

		map.activated
			.then(() => map.loaded)
			.then(() => {
				const REFLAYER = map.firstLabelLayer;

				map.hideAttribution();

				mapZoom = map.map.getZoom();
				map.map.on("zoom", () => {
					mapZoom = map.map.getZoom();
				});

				// luftbilder
				map.map.addSource(luftbilderId, {
					type: "raster",
					url: "https://interaktiv.tagesspiegel.de/maptiles/luftbilder_2022/tiles.json",
				});

				map.map.addLayer(
					{
						id: luftbilderId,
						source: luftbilderId,
						type: "raster",
					},
					REFLAYER
				);

				// bezirke
				map.addChoropleth(geoBezirke, {
					geoType: "geojson",
					id: "bezirke",
					borderColor: "#898695",
					borderWidth: 2,
					interactive: false,
					opacity: 0,
					beforeLayer: REFLAYER,
				});

				// labels
				const labelsOpts = {
					minZoom: 12.5,
					maxZoom: 14.5,
					fontSize: 12,
					color: "#fff",
					haloWidth: 1,
					haloColor: "#000",
					letterSpacing: 0.015,
					font: "Franklin Gothic Medium",
				};

				map.addLabels(labelsOrtsteile, {
					...labelsOpts,
					id: "labels-ortsteile",
					minZoom: 11.5,
					maxZoom: 12.5,
				});

				map.addLabels(labelsBezirke, {
					...labelsOpts,
					id: "labels-bezirke",
					minZoom: 9,
					maxZoom: 11.5,
				});

				// calculating weird scales for radius and label offset
				const sizeKey = "hours_possible_decimal";
				const labelOffsetDivisor = 14;
				const radii = {
					min: 2,
					"8": 1,
					"12": 4,
					"16": 8,
				};
				const circleRadius = [
					"interpolate",
					["linear"],
					["zoom"],
					8,
					["+", radii.min, radii["8"]],
					12,
					["+", radii.min, radii["12"]],
					16,
					["+", radii.min, radii["16"]],
				];
				const labelOffset = [
					"interpolate",
					["linear"],
					["zoom"],
					8,
					["+", radii.min, radii["8"] / labelOffsetDivisor],
					12,
					["+", radii.min, radii["12"] / labelOffsetDivisor],
					16,
					["+", radii.min, radii["16"] / labelOffsetDivisor],
				];
				// const circleRadius = circleScale({
				// 	key: sizeKey,
				// 	min: 2,
				// 	scale: [
				// 		[8, 1],
				// 		[12, 5],
				// 		[16, 10],
				// 	],
				// });
				// const circleRadius = circleScale({
				// 	key: sizeKey,
				// 	min: 5,
				// 	scale: [
				// 		[8, 1e-4],
				// 		[12, 0.5e-2],
				// 		[16, 1e-2],
				// 	],
				// });
				// const labelOffset = circleScale({
				// 	key: sizeKey,
				// 	min: 0.8,
				// 	scale: [
				// 		[8, 1e-4 / 15],
				// 		[12, 0.5e-2 / 15],
				// 		[16, 1e-2 / 15],
				// 	],
				// });

				map.addPoints(dataSchools, {
					id: pointsId,
					labels: true,
					labelMinZoom: 14.5,
					circlePaint: {
						"circle-radius": circleRadius,
						"circle-color": mapboxColorExpression,
						"circle-stroke-color": "#ffffff",
						"circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 9, 0.5, 15, 2],
						"circle-opacity": pointsBaseOpacity,
						"circle-opacity-transition": {
							duration: 3000,
						},
						"circle-stroke-opacity": 0.9,
					},
					labelOffset,
					labelKey: "name",
					tooltips: false,
					sortKeyCircles: sizeKey,
					tooltipsOnlyDesktop: true,
					mouseenterCallback: (f) => {
						if (isTouch) return;
						hoveredPoint = f.properties;
						addPopup(f);
					},
					mouseleaveCallback: () => {
						setTimeout(() => {
							if (!tooltipHovered) {
								hoveredPoint = undefined;
								removePopup();
								if (selectedPoint) addPopup(selectedPoint);
							}
						});
						removePopup();
					},
					onSelect: (f) => selectPoint(f),
					onReset: () => clearPoint(),
				});

				showBerlinMarkers();
				showRing();

				mapActivated = true;

				requestAnimationFrame(() => {
					map.map.moveLayer("water", "bezirke");
				});

				map.map.once("idle", () => {
					mapLoaded = true;

					// preselect a point
					const initialPoint = dataSchools.find((d) => d.bsn === "02G32");
					map.selectPoint(initialPoint, true);
				});
			});
	});

	// ///////////////////////////////////////////////////////////
	// apply category filter
	// ///////////////////////////////////////////////////////////

	$: if (mapActivated && mapLoaded) {
		const filterExp = ["in", "type", ...selectedCategoriesIds];
		map.map.setFilter(`${pointsId}`, filterExp);
		map.map.setFilter(`${pointsId}labels`, filterExp);
		// const opacityExp = ["case", ["in", ["get", "type"], ["literal", selectedCategoriesIds]], pointsBaseOpacity, 0];
		// map.map.setPaintProperty(`${pointsId}`, "circle-opacity", opacityExp);
		// map.map.setPaintProperty(`${pointsId}labels`, "text-opacity", opacityExp);

		// map.map.triggerRepaint();

		// if the selected point is not in the selected categories, clear it
		if (!selectedCategoriesIds.includes(selectedPoint?.type)) {
			clearPoint();
		}
	}

	// ///////////////////////////////////////////////////////////
	// map selection
	// ///////////////////////////////////////////////////////////
	let popup = null;

	const removePopup = () => {
		if (popup) popup.remove();
	};

	const addPopup = (feature) => {
		const props = feature.properties ? feature.properties : feature;

		if (popup) popup.remove();

		const html = `
				<div class="description">${categoriesById[props.type].labelSingular}</div>
				<div class="name">${props.name}</div>
		`;

		popup = new window.mapboxgl.Popup({
			color: "#1f1f1f",
			scale: 0.7,
			closeButton: false,
			closeOnClick: false,
			closeOnMove: false,
			className: "region-popup",
		})
			.setLngLat([props.lon, props.lat])
			.setHTML(html)
			.addTo(map.map);
	};

	// ///////////////////////////////////////////////////////////
	// markers
	// ///////////////////////////////////////////////////////////

	const berlinMarkers = [];

	function showBerlinMarkers() {
		map.activated
			.then(() => map.loaded)
			.then(() => {
				markerData.forEach((d) => {
					const melc = document.createElement("div");
					const mel = document.createElement("div");
					melc.appendChild(mel);
					mel.classList.add("marker");
					mel.classList.add(`marker--${d.n}`);
					mel.innerHTML = d.label || "";
					berlinMarkers.push(new window.mapboxgl.Marker(melc).setLngLat([d.lon, d.lat]).addTo(map.map));
				});
			});
	}

	let ringshown = false;
	let ringshownonce = false;
	let ringmarker;
	function showRing() {
		if (ringshown) return;
		ringshown = true;
		if (!ringshownonce) {
			ringshownonce = true;
			map.activated
				.then(() => map.loaded)
				.then(() => {
					map.map.addSource("sbahnring", {
						type: "geojson",
						data: geoRing,
					});
					map.map.addLayer({
						id: "sbahnring1",
						type: "line",
						source: "sbahnring",
						layout: {},
						paint: {
							"line-color": "#ffffff",
							"line-width": 3,
						},
					});
					map.map.addLayer({
						id: "sbahnring2",
						type: "line",
						source: "sbahnring",
						layout: {},
						paint: {
							"line-color": "#1f1f1f",
							"line-width": 2,
							"line-dasharray": [1, 1],
						},
					});
					const pos = [13.366688444522936, 52.543837986025096];
					const el = document.createElement("div");
					el.className = "ring-marker";
					el.innerHTML = !SCREENSHOT_MODE ? '<div class="ring-marker-text">S-Bahn<br>Ring</div>' : "";
					ringmarker = new window.mapboxgl.Marker(el).setLngLat(pos);
					ringmarker.addTo(map.map);
				});
		} else {
			map.map.setLayoutProperty("sbahnring1", "visibility", "visible");
			map.map.setLayoutProperty("sbahnring2", "visibility", "visible");
			ringmarker.addTo(map.map);
		}
	}

	function hideRing() {
		if (!ringshown) return;
		map.map.setLayoutProperty("sbahnring1", "visibility", "none");
		map.map.setLayoutProperty("sbahnring2", "visibility", "none");
		ringmarker.remove();
		ringshown = false;
	}

	// ///////////////////////////////////////////////////////////
	// search
	// ///////////////////////////////////////////////////////////
	let stopSearchMobile;
	let stopSearchDesktop;

	const stopSearch = (d) => {
		if (stopSearchMobile) stopSearchMobile(d);
		if (stopSearchDesktop) stopSearchDesktop(d);
	};

	const searchClearCbk = () => {
		map.toInitialView();
		removePopup();
		map.resetPoints();
	};

	const searchResultCbk = (d) => {
		if (d.type === "data") {
			map.selectPoint(d.datum, 15);
		} else {
			map.resetPoints();
			map.map.flyTo({
				center: [d.lng, d.lat],
				zoom: 15,
				padding: {
					left: 0,
					right: 0,
					bottom: 0,
					top: 0,
				},
			});
		}
	};

	// ///////////////////////////////////////////////////////////
	// tooltip
	// ///////////////////////////////////////////////////////////

	const closeClick = () => map.resetPoints();

	function tooltipClick() {}

	let tooltipExpanded = true;

	function tooltipMouseover() {
		if (window.innerWidth <= 544) return;
		tooltipHovered = true;
		if (selectedPoint) {
			hoveredPoint = undefined;
			removePopup();
			if (selectedPoint) addPopup(selectedPoint);
		}
	}

	function tooltipMouseout() {
		if (window.innerWidth <= 544) return;
		tooltipHovered = false;
	}

	$: console.log("selected", selectedPoint);
	$: console.log("hovered", hoveredPoint);
	$: console.log("selected categories", selectedCategoriesIds);
</script>

<MediaQuery />
<div class="schools-map" class:pw={paywall}>
	<div class="ui-container">
		<div class="search-and-filters">
			<div class="search mobile">
				<Search
					type={["geo", "data"]}
					iconGeo={"pin"}
					iconData={"house"}
					geoSearchBounds={initialBounds}
					dataSearchData={dataSchools}
					dataSearchKeys={["name"]}
					dataSearchGetLabel={(d) => d.name}
					dataSearchGetLat={(d) => d.lat}
					dataSearchGetLng={(d) => d.lon}
					placeholder="Schulname, Adresse, Ort eingeben"
					clearCallback={searchClearCbk}
					resultCallback={searchResultCbk}
					bind:stopSearch={stopSearchMobile}
				/>
			</div>
			<div class="filters">
				<Filter
					bind:values={selectedCategoriesIds}
					options={categories}
					includeAllOption={true}
					title="Schulen filtern"
					let:option
					let:active
				>
					<div class="filter-container" class:active>
						<div class="filter-label">{@html option?.labelShort || option.label}</div>
						{#if dataSchools}
							<div class="filter-number">
								{option.id === "all" ? dataSchools?.length : categoriesById[option.id].count}
							</div>
						{/if}
					</div>
				</Filter>
			</div>
		</div>
		<div class="legend-and-search">
			<div class="legend">
				<Legend />
			</div>
			<div class="search desktop">
				<Search
					type={["geo", "data"]}
					iconGeo={"pin"}
					iconData={"house"}
					geoSearchBounds={initialBounds}
					dataSearchData={dataSchools}
					dataSearchKeys={["name"]}
					dataSearchGetLabel={(d) => d.name}
					dataSearchGetLat={(d) => d.lat}
					dataSearchGetLng={(d) => d.lon}
					placeholder="Schulname, Adresse, Ort eingeben"
					clearCallback={searchClearCbk}
					resultCallback={searchResultCbk}
					bind:stopSearch={stopSearchDesktop}
				/>
			</div>
		</div>
	</div>

	<div
		class="map-container"
		class:tooltip-shown={tooltip}
		on:mouseleave={() => {
			// removePopup();
			// if (selectedRegion) addPopup(selectedRegion);
		}}
	>
		<div class="map" bind:this={elemMap} />

		{#if paywall}
			<div class="pw-map-image" />
		{/if}

		{#if !SCREENSHOT_MODE && !PWIMAGE_MODE}
			<div
				class="button-luftbilder"
				class:luftbilder={showLuftbilder}
				on:click={() => {
					showLuftbilder = !showLuftbilder;
				}}
			>
				<div class="button-luftbilder-image button-luftbilder-image-on" />
				<div class="button-luftbilder-image button-luftbilder-image-off" />
				<div class="button-luftbilder-text mobile">
					{showLuftbilder ? "Straßen-karte" : "Luft-bilder"}
				</div>
				<div class="button-luftbilder-text desktop">
					{showLuftbilder ? "Straßenkarte" : "Luftbilder"}
				</div>
			</div>
		{/if}
		<div class="map-loading" class:mapLoaded>
			<div class="ts-spinner">
				<div class="ts-double-bounce1" />
				<div class="ts-double-bounce2" />
			</div>
		</div>
		<!-- {#if selectedPoint} -->
		<!-- {#if selectedPoint || (hoveredPoint && !hoveredPoint.filtered)}
			{@const region = hoveredPoint || selectedPoint} -->
		{#if selectedPoint}
			{@const region = selectedPoint}
			<div
				class="tooltip-container"
				on:click={tooltipClick}
				on:mouseover={tooltipMouseover}
				on:focus={() => {}}
				on:blur={() => {}}
				on:mouseout={tooltipMouseout}
				class:expanded={tooltipExpanded}
				transition:fly={{ y: 100, duration: platform === "mobile" ? 400 : 0 }}
				bind:this={tooltip}
			>
				<Tooltip {region} onCloseClick={closeClick} />
			</div>
		{/if}
	</div>
	<div class="attribution">
		Daten:&nbsp;<a href="https://www.bildung.berlin.de/Schulverzeichnis/" target="_blank"
			>Schulverzeichnis der Senatsverwaltung für Bildung, Jugend und Familie</a
		>
		Kartenbasis:&nbsp;<a href="https://www.openmaptiles.org/" target="_blank">&copy;&nbsp;OpenMapTiles</a>
		<a href="https://www.openstreetmap.org/copyright" target="_blank">&copy;&nbsp;OpenStreetMap contributors</a>
		{#if showLuftbilder}
			| <a
				target="_blank"
				href="https://fbinter.stadt-berlin.de/fb/berlin/service.jsp?id=a_luftbild2022_true_rgbi@senstadt&amp;type=FEED"
				>Geoportal Berlin / Digitale farbige Orthophotos 2022 (DOP20RGBI)</a
			>
		{/if}
	</div>

	{#if paywall}
		<div class="pw-overlay" />
	{/if}
</div>

<style lang="scss">
	.schools-map {
		user-select: none;
		font-family: $ff-sans;
		font-size: $fs-base;
		line-height: $lh-base;
		color: $black;
	}

	.map-container {
		position: relative;
		margin-bottom: 0;
		transition: margin 0.3s ease;

		@media (max-width: $breakpoint-mobile-l) {
			&.tooltip-shown {
				margin-bottom: 170px;
			}
		}
	}

	.attribution {
		font-size: 14px;
		color: #666;
		line-height: 1.5em;
		margin-top: 12px;
		text-align: right;

		a {
			text-decoration: none;
		}
	}

	.map {
		position: relative;
		height: 650px;
	}

	.ui-container {
		max-width: 940px;
		margin: 0px auto;
	}

	.legend-and-search {
		display: flex;
		justify-content: space-between;
		margin-bottom: 5px;

		@media (max-width: $breakpoint-tablet-s) {
			flex-direction: column-reverse;
			margin-bottom: 5px;
		}

		@media (min-width: #{$breakpoint-tablet-s + 1px}) {
			align-items: center;
		}
	}

	.search {
		@media (max-width: $breakpoint-tablet-s) {
			width: 100%;
			margin-bottom: 13px;

			&.desktop {
				display: none;
			}
		}

		@media (min-width: #{$breakpoint-tablet-s + 1px}) {
			flex-basis: 250px;
			margin-bottom: 5px;

			&.mobile {
				display: none;
			}
		}
	}

	// desktop
	@media (min-width: #{$breakpoint-tablet-l + 1px}) {
	}

	// large tablets
	@media (min-width: #{$breakpoint-tablet-s + 1px}) and (max-width: $breakpoint-tablet-l) {
		.map {
			height: 650px;
		}
	}

	// small tablets
	@media (min-width: #{$breakpoint-mobile-l + 1px}) and (max-width: $breakpoint-tablet-s) {
		.map {
			height: 650px;
		}
	}

	// large/normal phones
	@media (min-width: #{$breakpoint-mobile-m + 1px}) and (max-width: $breakpoint-mobile-l) {
		.map {
			height: 460px;
		}
	}

	// small phones
	@media (min-width: #{$breakpoint-mobile-s + 1px}) and (max-width: $breakpoint-mobile-m) {
		.map {
			height: 420px;
		}
	}

	// very small phones
	@media (max-width: $breakpoint-mobile-s) {
		.map {
			height: 420px;
		}
	}

	:global(.marker) {
		pointer-events: none;
		background-position: center bottom;
		background-size: contain;
		background-repeat: no-repeat;
		position: absolute;
		bottom: 0;
		left: 0;
		transform: translateX(-50%);
		font-weight: $bold;
		font-size: 11px;
		font-family: $ff-sans;
		line-height: 14px;
		color: #fff;
		text-shadow: 1px 1px #1f1f1f, -1px 1px #1f1f1f, -1px -1px #1f1f1f, 1px -1px #1f1f1f, 0 1px #1f1f1f, 0 -1px #1f1f1f,
			1px 0 #1f1f1f, -1px 0 #1f1f1f, 0 0 2px rgba(0, 0, 0, 0.1);
	}

	// :global(.marker.marker--bbtor) {
	// 	background-image: url(../assets/images/berlin/bbtor.svg);
	// 	width: 26px;
	// 	height: 25px;
	// }

	// :global(.marker.marker--siegessaeule) {
	// 	display: none;
	// 	background-image: url(../assets/images/berlin/siegessaeule.svg);
	// 	width: 30px;
	// 	height: 30px;
	// }

	:global(.marker.marker--fernsehturm) {
		background-image: url(../assets/images/berlin/fernsehturm.svg);
		height: 45px;
		width: 20px;
	}

	// :global(.marker.marker--kaiserwilhelm) {
	// 	background-image: url(../assets/images/berlin/kaiserwilhelm.svg);
	// 	width: 30px;
	// 	height: 30px;
	// }

	// :global(.marker.marker--oberbaum) {
	// 	background-image: url(../assets/images/berlin/oberbaum.svg);
	// 	width: 35px;
	// 	height: 30px;
	// }

	:global(.ring-marker-text) {
		position: absolute;
		font-family: $ff-sans;
		white-space: nowrap;
		font-size: 11px;
		color: #272727;
		font-weight: $bold;
		text-shadow: 1px 1px #fff, -1px 1px #fff, 1px -1px #fff, -1px -1px #fff;
		transform: translate(-100%, -100%) !important;
		pointer-events: none;
		line-height: 14px;
		text-transform: uppercase;
		letter-spacing: 0.01em;
	}

	:global(.region-popup) {
		pointer-events: none !important;
	}

	:global(.region-popup .mapboxgl-popup-content) {
		border-radius: 4px;
		font-family: $ff-sans;
		text-align: center;
		padding: 7px 9px !important;
		pointer-events: none !important;
	}

	:global(.region-popup .name) {
		font-weight: $bold;
		letter-spacing: 0.015em;
		font-size: 13px;
		line-height: 14px;
	}

	:global(.region-popup .description) {
		letter-spacing: 0.015em;
		font-size: 12px;
		line-height: 15px;
		text-transform: capitalize;
	}

	// Anpassungen mapcontrols und scale
	// loading spinner

	.map {
		z-index: 0;
	}

	.map-loading {
		position: absolute;
		background: #f2f2f2;
		left: 0;
		top: 0;
		bottom: 0;
		right: 0;
		pointer-events: all;
		z-index: 1;
		transition: opacity 0.2s ease;
		opacity: 1;

		&.mapLoaded {
			opacity: 0;
			pointer-events: none;
		}
	}

	.ts-spinner {
		width: 40px;
		height: 40px;
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
	}

	.ts-double-bounce1,
	.ts-double-bounce2 {
		width: 100%;
		height: 100%;
		border-radius: 50%;
		background-color: #333;
		opacity: 0.6;
		position: absolute;
		top: 0;
		left: 0;
		animation: sk-bounce 2s infinite ease-in-out;
	}

	.ts-double-bounce2 {
		animation-delay: -1s;
	}

	@keyframes sk-bounce {
		0%,
		100% {
			transform: scale(0);
			-webkit-transform: scale(0);
		}

		50% {
			transform: scale(1);
			-webkit-transform: scale(1);
		}
	}

	// LUFTBILDER BUTTON

	.button-luftbilder {
		width: 48px;
		height: 48px;
		background: #fff;
		border-radius: 4px;
		box-shadow: 0 3px 5px rgba(0, 0, 0, 0.25);
		position: absolute;
		top: 10px;
		right: 20px;

		&:hover {
			filter: brightness(1.2);
		}

		&.hidden {
			display: none;
		}
	}

	.button-luftbilder-image {
		position: absolute;
		left: 4px;
		top: 4px;
		width: calc(100% - 8px);
		height: calc(100% - 8px);
		border-radius: 3px;
		background-size: cover;
		background-position: center center;
		overflow: hidden;
	}

	.button-luftbilder-image-on {
		display: none;
		background-image: url(../assets/images/map/icon_data_layer.jpg);
	}

	.button-luftbilder-image-off {
		background-image: url(../assets/images/map/icon_satellite_layer.jpg);
		display: block;
	}

	.button-luftbilder.luftbilder {
		.button-luftbilder-image-on {
			display: block;
		}

		.button-luftbilder-image-off {
			display: none;
		}
	}

	.button-luftbilder-text {
		color: #6a6a6a;
		font-weight: 600;
		font-size: 11px;
		line-height: 12px;
		text-align: center;
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translate(-50%, 5px);
		text-shadow: -1px 1px 0 #fff, 1px 1px 0 #fff, 1px -1px 0 #fff, -1px -1px 0 #fff;

		&.desktop {
			width: 84px;
		}
	}

	@media (min-width: #{$breakpoint-mobile-l + 1px}) {
		.button-luftbilder-text.mobile {
			display: none;
		}
	}

	@media (max-width: $breakpoint-mobile-l) {
		.button-luftbilder {
			width: 38px;
			height: 38px;
			top: 10px;
			right: 10px;
		}

		.button-luftbilder-image {
			left: 3px;
			top: 3px;
			width: calc(100% - 6px);
			height: calc(100% - 6px);
		}

		.button-luftbilder-text.desktop {
			display: none;
		}
	}

	// FILTERS
	.filters {
		margin-bottom: 13px;

		.filter-container {
			display: flex;
			column-gap: 6px;
			align-items: baseline;
			// color: $white;

			.filter-label {
				text-transform: capitalize;
			}

			.filter-number {
				font-size: 12px;
				opacity: 0.5;
			}
		}
	}

	// PW Overlay

	.pw-overlay {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		top: 0;
		background: transparent;
	}

	.pw-map-image {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		top: 0;
		background-image: url(../assets/images/paywall/pw-map-image-l--wide.jpg);
		background-size: cover;
		background-repeat: no-repeat;
		background-position: right center;
		filter: blur(2px);

		@media (min-width: #{$breakpoint-mobile-l + 1px}) and (max-width: $breakpoint-tablet-l) {
			background-position: right top;
			background-image: url(../assets/images/paywall/pw-map-image-m.jpg);
		}

		@media (max-width: $breakpoint-mobile-l) {
			background-position: center center;
			background-image: url(../assets/images/paywall/pw-map-image-s.jpg);
		}
	}
	@media (max-width: $breakpoint-mobile-l) {
		.pw {
			.map {
				height: 137vw !important;
			}
			.legend {
				display: none;
			}
		}
	}
</style>
